import { useState, useContext, useEffect, useRef } from "react"
import { navigate } from "gatsby"
import { LocaleContext } from "contexts"
import useTranslations from "hooks/useTranslations"
import { useForm } from "react-hook-form"
import { CalendarIcon } from "@heroicons/react/20/solid"
import {
  Input,
  TextArea,
  SubmitButton,
  PrivacyPolicyLink,
  Switch,
  RadioGroup,
  Select,
  ComboSelect,
} from "components/atoms/form"
import { Modal } from "components/atoms/common/Modal"
import { subscribeToSchoolTrainings, subscribeToWebinarPro } from "utils/forms"
import { toDateTimeFormat, toTimeFormat } from "utils/date"
import { siteCountry } from "utils/env"
import Altcha from "components/atoms/Altcha"
import countries from "../../../../config/i18n"

const locales = countries[siteCountry] || countries.fi

const schoolLevelOptions = {
  "fi-fi": [
    { title: "Alakoulu", id: "Primary" },
    { title: "Yläkoulu", id: "Secondary" },
    { title: "Toinen aste", id: "Upper secondary" },
    { title: "Yhtenäiskoulu", id: "Comprehensive school" },
  ],
  "sv-fi": [
    { title: "Lågstadiet", id: "Primary" },
    { title: "Högstadiet", id: "Secondary" },
    { title: "Andra stadiet", id: "Upper secondary" },
    { title: "Enhetsskola", id: "Comprehensive school" },
  ],
  "en-fi": [
    { title: "Primary school", id: "Primary" },
    { title: "Secondary school", id: "Secondary" },
    { title: "Upper secondary school", id: "Upper secondary" },
    { title: "Comprehensive school", id: "Comprehensive school" },
  ],
  "en-us": [
    { title: "Primary school", id: "Primary" },
    { title: "Secondary school", id: "Secondary" },
    { title: "Upper secondary school", id: "Upper secondary" },
    { title: "Comprehensive school", id: "Comprehensive school" },
  ],
}

const cities = {
  "fi-fi": [
    { title: "Espoo", id: "Espoo" },
    { title: "Eurajoki", id: "Eurajoki" },
    { title: "Hollola", id: "Hollola" },
    { title: "Ii", id: "Ii" },
    { title: "Janakkala", id: "Janakkala" },
    { title: "Kankaanpää", id: "Kankaanpää" },
    { title: "Kauniainen", id: "Kauniainen" },
    { title: "Kirkkonummi", id: "Kirkkonummi" },
    { title: "Kemi", id: "Kemi" },
    { title: "Laitila", id: "Laitila" },
    { title: "Lempäälä", id: "Lempäälä" },
    { title: "Loppi", id: "Loppi" },
    { title: "Naantali", id: "Naantali" },
    { title: "Rauma", id: "Rauma" },
    { title: "Rovaniemi", id: "Rovaniemi" },
    { title: "Säkylä", id: "Säkylä" },
    { title: "Tammela", id: "Tammela" },
    { title: "Tampere", id: "Tampere" },
    { title: "Uusikaupunki", id: "Uusikaupunki" },
    { title: "Vantaa", id: "Vantaa" },
    { title: "Vihti", id: "Vihti" },
    { title: "Vörå", id: "Vörå" },
    { title: "Äänekoski", id: "Äänekoski" },
    // { title: "Orimattila", id: "Orimattila" },
    // { title: "Turku", id: "Turku" },
  ],
  "sv-fi": [
    { title: "Esbo", id: "Espoo" },
    { title: "Euraåminne", id: "Euraåminne" },
    { title: "Grankulla", id: "Kauniainen" },
    { title: "Hollola", id: "Hollola" },
    { title: "Ii", id: "Ii" },
    { title: "Janakkala", id: "Janakkala" },
    { title: "Kankaanpää", id: "Kankaanpää" },
    { title: "Kemi", id: "Kemi" },
    { title: "Kyrkslätt", id: "Kirkkonummi" },
    { title: "Lempäälä", id: "Lempäälä" },
    { title: "Letala", id: "Laitila" },
    { title: "Loppis", id: "Loppis" },
    { title: "Nådendal", id: "Nådendal" },
    { title: "Nystad", id: "Uusikaupunki" },
    { title: "Raumo", id: "Rauma" },
    { title: "Rovaniemi", id: "Rovaniemi" },
    { title: "Säkylä", id: "Säkylä" },
    { title: "Tammela", id: "Tammela" },
    { title: "Tammerfors", id: "Tampere" },
    { title: "Vanda", id: "Vantaa" },
    { title: "Vichtis", id: "Vichtis" },
    { title: "Vörå", id: "Vörå" },
    { title: "Äänekoski", id: "Äänekoski" },
    // { title: "Orimattila", id: "Orimattila" },
    // { title: "Åbo", id: "Turku" },
  ],
  "en-fi": [
    { title: "Espoo", id: "Espoo" },
    { title: "Eurajoki", id: "Eurajoki" },
    { title: "Hollola", id: "Hollola" },
    { title: "Ii", id: "Ii" },
    { title: "Janakkala", id: "Janakkala" },
    { title: "Kankaanpää", id: "Kankaanpää" },
    { title: "Kauniainen", id: "Kauniainen" },
    { title: "Kemi", id: "Kemi" },
    { title: "Kirkkonummi", id: "Kirkkonummi" },
    { title: "Laitila", id: "Laitila" },
    { title: "Lempäälä", id: "Lempäälä" },
    { title: "Loppi", id: "Loppi" },
    { title: "Naantali", id: "Naantali" },
    { title: "Rauma", id: "Rauma" },
    { title: "Rovaniemi", id: "Rovaniemi" },
    { title: "Säkylä", id: "Säkylä" },
    { title: "Tammela", id: "Tammela" },
    { title: "Tampere", id: "Tampere" },
    { title: "Uusikaupunki", id: "Uusikaupunki" },
    { title: "Vantaa", id: "Vantaa" },
    { title: "Vihti", id: "Vihti" },
    { title: "Vörå", id: "Vörå" },
    { title: "Äänekoski", id: "Äänekoski" },
    // { title: "Orimattila", id: "Orimattila" },
    // { title: "Turku", id: "Turku" },
  ],
}

const SubscribeForm = ({ selected, items = [], setSelected, segment = "" }) => {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm()

  const {
    formFieldName,
    formFieldEmail,
    formFieldJobTitle,
    formFieldCity,
    formFieldSchool,
    formFieldSchoolLevel,
    formFieldDate,
    formFieldMessage,
    formFieldOrganization,
    formFieldQuantity,
    formButtonSubmit,
    formErrorGeneral,
    formErrorUserExist,
    formErrorContactUs,
    formFieldPhone,
    formFieldOnlineAttendance,
    formFieldLunch,
    formFieldLunchDescription,
    formFieldSometurvaUsage,
    formFieldSometurvaUsageOption1,
    formFieldSometurvaUsageOption2,
    formFieldSometurvaUsageOption3,
    schoolsTrainingNewsletter,
    webinarPrivacyPolicy,
  } = useTranslations()

  const { locale } = useContext(LocaleContext)

  const [isSubmitError, setSubmitError] = useState(false)
  const [errMessage, setErrMessage] = useState(formErrorGeneral)
  const altchaRef = useRef<{ value: string | null }>(null)

  useEffect(() => {
    setValue("date", selected)
  }, [selected])

  const onSubmit = async (data) => {
    try {
      let res
      const altcha = altchaRef.current?.value
      const skipFormSubmit = false
      if (process.env.NODE_ENV === "development" && skipFormSubmit) {
        res = { status: 200 }
      } else {
        res =
          segment === "schools" || segment === "professionals"
            ? await subscribeToSchoolTrainings(data, locale, altcha)
            : await subscribeToWebinarPro(data, locale, altcha)
      }

      if (res.status === 200) {
        // setShowModal(true)
        // reset()
        setSubmitError(false)
        const successPage = "/schools/trainings/success"
        const path = locales[locale].default
          ? successPage
          : `/${locales[locale].path}${successPage}`
        navigate(path, {
          state: {
            title: selected.title,
            start: selected.time,
            end: selected.end,
            description: selected.sections,
            url: selected.url,
            segment,
          },
        })
      }
    } catch ({ response }) {
      if (response?.data?.title === "Invalid Resource") {
        setErrMessage(response.data.detail)
      } else if (response?.data?.title === "Member Exists") {
        setErrMessage(formErrorUserExist)
      }
      setSubmitError(true)
    }
  }

  const isSpecialWebinar = selected.audience === "special"
  const isHybridAttendance = selected.attendanceType === "hybrid"

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mb-0 space-y-4 bg-white px-4 pb-8 pt-6"
    >
      {items.length > 1 && (
        <Select
          name="date"
          items={items}
          label={formFieldDate}
          onChange={setSelected}
          control={control}
          required
        />
      )}
      <Input
        label={formFieldName}
        name="name"
        control={control}
        required={isHybridAttendance}
      />
      <Input
        label={formFieldEmail}
        name="email"
        required
        type="email"
        control={control}
      />
      {segment === "schools" ? (
        <>
          {isSpecialWebinar ? (
            <ComboSelect
              name="city"
              items={cities[locale]}
              label={formFieldCity}
              control={control}
              required
            />
          ) : (
            <Select
              name="city"
              items={cities[locale]}
              label={formFieldCity}
              control={control}
              required
            />
          )}
          <Input
            label={formFieldSchool}
            name="school"
            required={isSpecialWebinar}
            control={control}
          />
          <Select
            name="grade"
            items={schoolLevelOptions[locale]}
            label={formFieldSchoolLevel}
            control={control}
          />
        </>
      ) : (
        <Input
          label={formFieldOrganization}
          name="org"
          control={control}
          required={isHybridAttendance}
        />
      )}
      <Input
        label={formFieldPhone}
        control={control}
        type="tel"
        name="phone"
        errors={errors}
        defaultValue="+358"
        required={isHybridAttendance}
      />
      {isSpecialWebinar && (
        <Input
          label={formFieldQuantity}
          name="quantity"
          type="number"
          control={control}
          required
        />
      )}
      {!isSpecialWebinar && (
        <Input label={formFieldJobTitle} name="job" control={control} />
      )}

      {isHybridAttendance ? (
        <Switch
          label={formFieldOnlineAttendance}
          name="onlineAttendance"
          control={control}
        />
      ) : null}
      {segment === "professionals" ? (
        <RadioGroup
          label={formFieldSometurvaUsage}
          name="someturvaUsage"
          control={control}
          required
          options={[
            { id: "already-in-use", label: formFieldSometurvaUsageOption1 },
            {
              id: "not-in-use-want-info",
              label: formFieldSometurvaUsageOption2,
            },
            { id: "not-in-use-no-info", label: formFieldSometurvaUsageOption3 },
          ]}
        />
      ) : selected.showLunchOption ? (
        <Switch
          label={formFieldLunch}
          description={formFieldLunchDescription}
          name="orderLunch"
          control={control}
        />
      ) : null}
      <TextArea
        register={register}
        name="message"
        label={formFieldMessage}
        rows={2}
      />
      {segment === "schools" ? (
        // <Switch
        //   label={formFieldCertificate}
        //   name="certificate"
        //   errors={errors}
        //   control={control}
        // />
        <Switch
          label={schoolsTrainingNewsletter}
          name="somevahti"
          control={control}
        />
      ) : null}
      <div className="text-sm font-bold text-zinc-700 ">
        <Altcha
          ref={altchaRef}
          style={{
            "--altcha-border-radius": "0.375rem",
            "--altcha-color-border": "rgb(212, 212, 216)",
            "--altcha-color-active": "teal",
            "--altcha-color-border-focus": "#2563eb",
            "--altcha-color-base": "rgba(255, 255, 255, 0.5)",
          }}
        />
      </div>
      <PrivacyPolicyLink text={webinarPrivacyPolicy} />
      <SubmitButton
        label={formButtonSubmit}
        isSubmitting={isSubmitting}
        isSubmitSuccessful={isSubmitSuccessful}
        isSubmitError={isSubmitError}
        errorMessage={`${errMessage} ${formErrorContactUs} ${locales[locale].contactEmail}`}
      />
    </form>
  )
}

export default function FormTrainings({
  webinar,
  webinars,
  setActiveWebinar,
  segment,
}) {
  const [showModal, setShowModal] = useState(false)
  const { locale } = useContext(LocaleContext)
  const {
    webinarFormSuccessTitle,
    webinarFormSuccessBody,
    trainingFormSuccessBody,
    webinarFormSuccessButton,
  } = useTranslations()
  return (
    <div className="m-0 mx-auto ml-auto max-w-lg" id="webinar-form">
      <div className="overflow-hidden rounded-lg transition duration-150 ease-in-out">
        {/* content */}
        <div className="flex grow flex-col bg-zinc-700 px-4 pb-7 pt-6 sm:px-6">
          <div className="my-3 font-display text-2xl font-bold leading-7 text-white">
            {webinar.title}
          </div>
        </div>
        {/* footer */}
        <div className="bg-kkm-green px-4 py-4 sm:flex sm:justify-between sm:px-6">
          <div className="flex items-center text-sm leading-5 text-zinc-800 sm:mr-6">
            {/* footer left */}
            <CalendarIcon className="mr-1.5 h-5 w-5 text-zinc-800" />
            <span className="font-bold">
              <time dateTime={`${webinar.time}`}>
                {toDateTimeFormat(webinar.time, locale)}
                {webinar.end ? ` – ${toTimeFormat(webinar.end)}` : null}
              </time>
            </span>
          </div>
        </div>
        {/* form */}
        <SubscribeForm
          items={webinars}
          selected={webinar}
          setSelected={setActiveWebinar}
          segment={segment}
        />
        <Modal
          isOpen={showModal}
          setIsOpen={setShowModal}
          title={webinarFormSuccessTitle}
          bodyText={
            segment === "schools"
              ? trainingFormSuccessBody
              : webinarFormSuccessBody
          }
          buttonText={webinarFormSuccessButton}
        />
      </div>
    </div>
  )
}
